/**
 * Google Play Billing wrapper for TWA.
 * Uses the Digital Goods API (available inside signed TWAs) + Payment Request API with
 * "https://play.google.com/billing" supportedMethods. Non-consumable "lifetime" product.
 *
 * Works ONLY inside the signed Android TWA (Chrome Custom Tab TWA). Returns `false`/throws
 * when running in a regular browser — the caller is expected to fall back to Stripe.
 */

export const PRODUCT_ID = "lifetime_access_990";
const PLAY_BILLING_METHOD = "https://play.google.com/billing";

async function getService() {
  if (typeof window === "undefined") return null;
  if (!window.getDigitalGoodsService) return null;
  try {
    return await window.getDigitalGoodsService(PLAY_BILLING_METHOD);
  } catch (e) {
    console.warn("[playBilling] getDigitalGoodsService failed:", e);
    return null;
  }
}

export async function isPlayBillingAvailable() {
  try {
    if (typeof window === "undefined") return false;
    if (!window.getDigitalGoodsService) return false;
    if (typeof window.PaymentRequest !== "function") return false;
    const svc = await getService();
    return !!svc;
  } catch {
    return false;
  }
}

/**
 * Lists ALL currently owned purchases visible to the TWA via Digital Goods.
 * Non-consumable products (like `lifetime_access_990`) remain here forever once purchased.
 * Returns [] when running outside the TWA or on failure.
 */
export async function listExistingPurchases() {
  const svc = await getService();
  if (!svc || typeof svc.listPurchases !== "function") return [];
  try {
    const raw = await svc.listPurchases();
    if (!Array.isArray(raw)) return [];
    return raw
      .map((p) => {
        // Chrome's Digital Goods API has gone through v1/v2 shape changes — be tolerant.
        const d = p && p.details ? p.details : {};
        return {
          productId: p?.itemId || d.itemId || p?.productId || d.productId || null,
          purchaseToken: p?.purchaseToken || d.purchaseToken || p?.token || d.token || null,
          acknowledged: Boolean(p?.acknowledged ?? d.acknowledged ?? false),
        };
      })
      .filter((p) => p.productId && p.purchaseToken);
  } catch (e) {
    console.warn("[playBilling] listPurchases failed:", e);
    return [];
  }
}

/**
 * Returns the owned `lifetime_access_990` purchase (if any) or null.
 */
export async function findExistingLifetimePurchase(productId = PRODUCT_ID) {
  const purchases = await listExistingPurchases();
  return purchases.find((p) => p.productId === productId) || null;
}

/**
 * Launches the native Google Play purchase sheet, returning { purchaseToken, productId, orderId? }
 * on success. Never trust the result on the client — the backend must re-verify via Play Developer API.
 */
export async function buyLifetimeAccess(productId = PRODUCT_ID) {
  const svc = await getService();
  if (!svc) throw new Error("Digital Goods API indisponível neste dispositivo.");

  const items = await svc.getDetails([productId]);
  const details = (items && items[0]) || null;
  if (!details) throw new Error("Produto não encontrado na Google Play Store.");

  const paymentRequest = new PaymentRequest(
    [
      {
        supportedMethods: PLAY_BILLING_METHOD,
        data: { sku: productId },
      },
    ],
    {
      total: {
        label: details.title || "Acesso vitalício",
        amount: {
          currency: (details.price && details.price.currency) || "BRL",
          value: (details.price && details.price.value) || "9.90",
        },
      },
    }
  );

  const response = await paymentRequest.show();
  // The purchase token lives in response.details.token (Chrome's TWA integration)
  const purchaseToken =
    (response.details && (response.details.token || response.details.purchaseToken)) || null;
  if (!purchaseToken) {
    await response.complete("fail");
    throw new Error("Compra não retornou purchaseToken.");
  }
  await response.complete("success");
  return {
    purchaseToken,
    productId,
    orderId: response.details && response.details.orderId,
  };
}
